import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PlusCircle, Search, Home, User, Wallet } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { useGuestSaves } from "@/hooks/useGuestSaves";
import GuestRescueSheet from "@/components/auth/GuestRescueSheet";

const BottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();
  const guestSaves = useGuestSaves();
  const [rescueOpen, setRescueOpen] = useState(false);

  if (location.pathname === "/trips") {
    return null;
  }

  const showGuestBadge = !user && guestSaves.totalCount > 0;

  const items = user
    ? [
        { to: "/", icon: Home, label: t("nav.bottom.home") },
        { to: "/search", icon: Search, label: t("nav.bottom.explore") },
        { to: "/trips", icon: PlusCircle, label: t("nav.bottom.newTrip"), primary: true },
        { to: "/budget", icon: Wallet, label: t("nav.bottom.budget") },
        { to: "/profile", icon: User, label: t("nav.bottom.profile") },
      ]
    : [
        { to: "/", icon: Home, label: t("nav.bottom.home") },
        { to: "/search", icon: Search, label: t("nav.bottom.explore") },
        { to: "/trips", icon: PlusCircle, label: t("nav.bottom.newTrip"), primary: true },
        { to: "/login", icon: User, label: t("nav.bottom.login"), badge: showGuestBadge ? guestSaves.totalCount : 0 },
      ];

  return (
    <>
      <nav
        data-hide-on-keyboard="true"
        className="md:hidden fixed bottom-0 left-0 right-0 z-floating-ui bg-card/95 backdrop-blur-lg border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 8px)" }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-around h-[4.25rem] px-2">
          {items.map((item) => {
            const isActive = item.to === "/"
              ? location.pathname === "/"
              : location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            const isPrimary = "primary" in item && item.primary;
            const badgeCount = "badge" in item ? (item.badge as number) : 0;
            const isGuestProfile = !user && item.to === "/login" && badgeCount > 0;

            const renderIcon = () => (
              <div className="relative">
                {isPrimary ? (
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-glow -mt-3">
                    <item.icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                ) : (
                  <>
                    <item.icon className={`h-[22px] w-[22px] transition-transform ${isActive ? "stroke-[2.5] scale-110" : ""}`} />
                    {isActive && (
                      <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                    )}
                    {badgeCount > 0 && (
                      <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center shadow-md ring-2 ring-card animate-pulse">
                        {badgeCount > 9 ? "9+" : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </div>
            );

            const labelEl = (
              <span className={`text-[11px] font-semibold leading-none whitespace-nowrap ${isPrimary ? "text-primary font-bold" : isActive ? "text-primary" : ""}`}>
                {item.label}
              </span>
            );

            const baseClass = `flex flex-col items-center justify-center gap-1 rounded-xl transition-all min-w-[56px] min-h-[48px] px-2 py-2 active:scale-95 ${
              isPrimary
                ? "text-primary-foreground"
                : isActive
                  ? "text-primary"
                  : "text-muted-foreground active:bg-primary/10"
            }`;

            if (isGuestProfile) {
              // Tap profile-icon as guest with saves → open rescue sheet instead of /login
              return (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => setRescueOpen(true)}
                  aria-label={item.label}
                  className={baseClass}
                >
                  {renderIcon()}
                  {labelEl}
                </button>
              );
            }

            return (
              <Link
                key={item.to}
                to={item.to}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
                className={baseClass}
              >
                {renderIcon()}
                {labelEl}
              </Link>
            );
          })}
        </div>
      </nav>

      <GuestRescueSheet open={rescueOpen} onOpenChange={setRescueOpen} />
    </>
  );
};

export default BottomNav;
